import { useState, useEffect } from 'react';
import { UserPlus, Bug, Menu as MenuIcon } from 'lucide-react';
import logo from '../../assets/logo.png';
import LanguageSwitcher from '../LanguageSwitcher';
import { useTranslation } from '../../context/LanguageContext';
import AddContactModal from './AddContactModal';

/**
 * Debug info component - shows auth token and storage key status
 */
const DebugInfo = () => {
    const [showDebug, setShowDebug] = useState(false);
    const [debugData, setDebugData] = useState({});
    const [fixing, setFixing] = useState(false);
    const [fixStatus, setFixStatus] = useState('');

    const updateDebug = () => {
        const authToken = localStorage.getItem('authToken') || '';
        const linkooUser = localStorage.getItem('linkoo_user');
        let user = null;
        try {
            user = linkooUser ? JSON.parse(linkooUser) : null;
        } catch (e) {}

        setDebugData({
            token: authToken ? authToken.substring(0, 15) + '...' : 'MISSING',
            tokenType: authToken?.startsWith('eyJ') ? 'JWT' : (authToken ? 'placeholder' : 'none'),
            peopleUuid: user?.peopleUuid ? user.peopleUuid.substring(0, 8) + '...' : 'MISSING',
            storageKey: user?.storageKey ? 'OK' : 'MISSING',
            hasLinkooUser: !!user
        });
    };

    useEffect(() => {
        updateDebug();
        window.addEventListener('storage', updateDebug);
        return () => window.removeEventListener('storage', updateDebug);
    }, []);

    const handleFix = async () => {
        setFixing(true);
        setFixStatus('Fetching...');
        try {
            const userId = localStorage.getItem('userId');
            const userUuid = localStorage.getItem('userUuid');
            const userEmail = localStorage.getItem('userEmail');
            const userName = localStorage.getItem('userName');

            if (!userUuid) {
                setFixStatus('ERROR: userUuid missing');
                setFixing(false);
                return;
            }

            // Fetch user data from /peoples/{uuid}
            const response = await fetch(`https://apilinkoo.creago.be/rest/linkoo/peoples/${userUuid}`);
            const text = await response.text();

            if (!text) {
                setFixStatus('ERROR: Empty response');
                setFixing(false);
                return;
            }

            let result;
            try {
                result = JSON.parse(text);
            } catch (parseErr) {
                setFixStatus('ERROR: Invalid JSON - ' + text.substring(0, 50));
                setFixing(false);
                return;
            }

            if (result.success || result.SUCCESS) {
                const userData = result.data || result.DATA;

                if (userData) {
                    const storageKey = userData.storagekey || userData.storageKey || userData.STORAGEKEY;
                    const peopleUuid = userData.peopleuuid || userData.peopleUuid || userData.PEOPLEUUID || userUuid;

                    const linkooUser = {
                        id: parseInt(userId, 10),
                        email: userEmail,
                        firstName: userData.firstname || userData.firstName || userName?.split(' ')[0] || '',
                        lastName: userData.lastname || userData.lastName || userName?.split(' ').slice(1).join(' ') || '',
                        peopleUuid: peopleUuid,
                        storageKey: storageKey || ''
                    };

                    localStorage.setItem('linkoo_user', JSON.stringify(linkooUser));
                    setFixStatus(storageKey ? 'OK! Reload page' : 'OK but no storageKey');
                    updateDebug();
                } else {
                    setFixStatus('ERROR: User not found');
                }
            } else {
                setFixStatus('ERROR: ' + (result.message || 'API error'));
            }
        } catch (err) {
            setFixStatus('ERROR: ' + err.message);
        }
        setFixing(false);
    };

    return (
        <div className="relative">
            <button
                onClick={() => setShowDebug(!showDebug)}
                className={`p-1 rounded ${showDebug ? 'bg-yellow-200' : 'hover:bg-gray-100'}`}
                title="Debug info"
            >
                <Bug className={`w-4 h-4 ${debugData.tokenType === 'JWT' && debugData.storageKey === 'OK' ? 'text-green-500' : 'text-red-500'}`} />
            </button>
            {showDebug && (
                <div className="absolute top-8 left-0 z-50 bg-gray-900 text-xs font-mono p-3 rounded-lg shadow-xl min-w-[220px]">
                    <div className="space-y-1">
                        <div>
                            <span className="text-gray-400">token:</span>{' '}
                            <span className={debugData.tokenType === 'JWT' ? 'text-green-400' : 'text-red-400'}>
                                {debugData.token}
                            </span>
                            <span className={`ml-1 px-1 rounded text-[10px] ${debugData.tokenType === 'JWT' ? 'bg-green-800' : 'bg-red-800'}`}>
                                {debugData.tokenType}
                            </span>
                        </div>
                        <div>
                            <span className="text-gray-400">linkoo_user:</span>{' '}
                            <span className={debugData.hasLinkooUser ? 'text-green-400' : 'text-red-400'}>
                                {debugData.hasLinkooUser ? 'OK' : 'MISSING'}
                            </span>
                        </div>
                        <div>
                            <span className="text-gray-400">peopleUuid:</span>{' '}
                            <span className={debugData.peopleUuid !== 'MISSING' ? 'text-purple-400' : 'text-red-400'}>
                                {debugData.peopleUuid}
                            </span>
                        </div>
                        <div>
                            <span className="text-gray-400">storageKey:</span>{' '}
                            <span className={debugData.storageKey === 'OK' ? 'text-green-400' : 'text-red-400'}>
                                {debugData.storageKey}
                            </span>
                        </div>
                        {(!debugData.hasLinkooUser || debugData.storageKey === 'MISSING') && (
                            <div className="pt-2 border-t border-gray-700 mt-2">
                                <button
                                    onClick={handleFix}
                                    disabled={fixing}
                                    className="w-full bg-yellow-600 hover:bg-yellow-500 text-white px-2 py-1 rounded text-xs disabled:opacity-50"
                                >
                                    {fixing ? 'Fixing...' : 'Fix linkoo_user'}
                                </button>
                                {fixStatus && (
                                    <div className={`mt-1 text-[10px] ${fixStatus.startsWith('OK') ? 'text-green-400' : 'text-red-400'}`}>
                                        {fixStatus}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

/**
 * Header Component
 * Displays the Linkoo logo on the left and add contact button on the right
 * @param {Object} props
 * @param {Function} props.onContactAdded - Callback when a contact is added
 * @param {Function} props.onToggleMenu - Function to toggle mobile menu
 * @param {boolean} props.showMenuButton - Whether to show the menu button
 */
const Header = ({ onContactAdded, onToggleMenu, showMenuButton = false }) => {
    const { t } = useTranslation();
    const [isAddContactModalOpen, setIsAddContactModalOpen] = useState(false);

    // Get current user from localStorage
    const currentUser = {
        id: parseInt(localStorage.getItem('userId') || '0', 10),
        uuid: localStorage.getItem('userUuid') || '',
        email: localStorage.getItem('userEmail') || '',
        name: localStorage.getItem('userName') || ''
    };

    // Check if user is admin (check localStorage first, then fallback to linkoo_user)
    let userRole = localStorage.getItem('userRole') || '';
    if (!userRole) {
        try {
            const linkooUser = JSON.parse(localStorage.getItem('linkoo_user') || '{}');
            userRole = linkooUser.role || '';
        } catch (e) {}
    }
    const isAdmin = userRole.toUpperCase() === 'ADMIN';

    return (
        <div className="flex items-center justify-between mb-4 gap-2">
            {/* Left side: hamburger + logo */}
            <div className="flex items-center gap-2 min-w-0">
                {/* Mobile menu button */}
                {showMenuButton && (
                    <button
                        onClick={onToggleMenu}
                        className="sm:hidden flex-shrink-0 w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center text-linkoo-purple hover:bg-gray-200 transition-colors"
                        aria-label="Ouvrir le menu"
                    >
                        <MenuIcon className="w-6 h-6" />
                    </button>
                )}
                <img
                    src={logo}
                    alt="Linkoo"
                    className="h-10 sm:h-15 object-contain flex-shrink-0"
                />
                {isAdmin && <DebugInfo />}
            </div>

            {/* Right side: language + add contact */}
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                <LanguageSwitcher variant="compact" />
                <button
                    onClick={() => setIsAddContactModalOpen(true)}
                    className="flex items-center gap-2 bg-gradient-to-r from-linkoo-purple to-linkoo-pink text-white px-3 sm:px-4 py-2 rounded-lg font-medium hover:opacity-90 transition-opacity"
                >
                    <UserPlus className="w-5 h-5" />
                    <span className="hidden sm:inline">{t.dashboard?.addContact || 'Ajouter un contact'}</span>
                </button>
            </div>

            {/* Add Contact Modal */}
            <AddContactModal
                isOpen={isAddContactModalOpen}
                onClose={() => setIsAddContactModalOpen(false)}
                currentUser={currentUser}
                onContactAdded={(contact) => {
                    onContactAdded?.(contact);
                    setIsAddContactModalOpen(false);
                }}
            />
        </div>
    );
};

export default Header;
